import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchClientNotes,
  createClientNote,
  updateClientNote,
  deleteClientNote,
} from '@/services/clientNotesService';
import type { ClientNote } from '@/types/client';
import { clientQueryKeys } from './useClients';

export const clientNoteQueryKeys = {
  all: (clientId: string) => [...clientQueryKeys.all, 'notes', clientId] as const,
};

export const useFetchClientNotes = (clientId: string | null) => {
  return useQuery<ClientNote[], Error>({
    queryKey: clientNoteQueryKeys.all(clientId!),
    queryFn: () => fetchClientNotes(clientId!),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useCreateClientNote = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation<ClientNote, Error, string>({
    mutationFn: (content: string) => createClientNote(clientId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientNoteQueryKeys.all(clientId) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.activityFeed(clientId) });
    },
  });
};

export const useUpdateClientNote = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation<ClientNote, Error, { noteId: string; updates: { content?: string; pinned?: boolean } }>({
    mutationFn: ({ noteId, updates }) => updateClientNote(noteId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientNoteQueryKeys.all(clientId) });
    },
  });
};

export const useDeleteClientNote = (clientId: string) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (noteId: string) => deleteClientNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientNoteQueryKeys.all(clientId) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.activityFeed(clientId) });
    },
  });
};
